import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Download, X, Smartphone } from "lucide-react";
import { onInstallAvailable, requestInstall, isStandalone } from "../../lib/pwa";

/**
 * Floating "Add to home screen" banner for Android / Chrome. Appears only
 * when the browser fires `beforeinstallprompt` and the admin isn't already
 * running inside a standalone PWA shell. Dismissed state is remembered for
 * 7 days in localStorage so a user who said "not now" is not nagged.
 *
 * iOS Safari never fires `beforeinstallprompt`; for iOS a one-time hint
 * card is shown with the Add-to-Home-Screen share instructions.
 */
const DISMISS_KEY = "admin_pwa_dismissed_until";
const DISMISS_MS = 7 * 24 * 60 * 60 * 1000;

export default function InstallPrompt() {
  const [available, setAvailable] = useState(false);
  const [installing, setInstalling] = useState(false);
  const [dismissed, setDismissed] = useState(() => {
    const v = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return Date.now() < v;
  });
  const [iosHint, setIosHint] = useState(false);

  useEffect(() => {
    if (isStandalone()) return;                       // already installed as PWA
    const off = onInstallAvailable(setAvailable);
    // iOS has no event; detect and show the hint once if the user never saw it.
    const ua = navigator.userAgent || "";
    const isIOS = /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
    const isSafari = /^((?!chrome|android|crios|fxios).)*safari/i.test(ua);
    if (isIOS && isSafari && !dismissed) setIosHint(true);
    return off;
  }, [dismissed]);

  const install = async () => {
    setInstalling(true);
    const res = await requestInstall();
    setInstalling(false);
    if (res.outcome === "accepted") toast.success("✓ Panel ana ekrana eklendi");
    else if (res.outcome === "dismissed") toast("Kurulum iptal edildi");
    else if (res.outcome === "unavailable") toast.error("Tarayıcı şu an kurulum teklif etmiyor. Chrome menüsünden 'Uygulamayı yükle'yi deneyin.");
  };

  const dismiss = () => {
    localStorage.setItem(DISMISS_KEY, String(Date.now() + DISMISS_MS));
    setDismissed(true);
    setAvailable(false);
    setIosHint(false);
  };

  if (dismissed || isStandalone()) return null;
  if (!available && !iosHint) return null;

  return (
    <div
      className="fixed bottom-4 inset-x-4 sm:inset-x-auto sm:right-6 sm:left-auto sm:max-w-sm z-[9999]
                 bg-gradient-to-br from-indigo-600 to-violet-700 text-white rounded-2xl shadow-2xl
                 border border-white/10 p-4 flex items-start gap-3 animate-in slide-in-from-bottom-4 duration-300"
      data-testid="pwa-install-banner"
    >
      <div className="shrink-0 w-10 h-10 rounded-xl bg-white/15 flex items-center justify-center">
        <Smartphone size={20} />
      </div>
      <div className="min-w-0 flex-1">
        <p className="font-semibold text-sm">Admin paneli ana ekranına kur</p>
        {iosHint ? (
          <p className="text-xs text-white/90 mt-0.5 leading-relaxed">
            Safari → Paylaş <span className="font-mono">⬆︎</span> → <strong>"Ana Ekrana Ekle"</strong>
            {" "}deyin. Tam ekran bir uygulama gibi açılacak.
          </p>
        ) : (
          <>
            <p className="text-xs text-white/90 mt-0.5 leading-relaxed">
              Tam ekran, hızlı ve bildirimli bir uygulama olarak kurun.
            </p>
            <button
              onClick={install}
              disabled={installing}
              className="mt-2 bg-white text-indigo-700 font-semibold text-sm rounded-lg px-3 py-1.5 inline-flex items-center gap-1.5 hover:bg-indigo-50 disabled:opacity-50 transition"
              data-testid="pwa-install-btn"
            >
              <Download size={14} />{installing ? "Kuruluyor..." : "Kur"}
            </button>
          </>
        )}
      </div>
      <button
        onClick={dismiss}
        className="shrink-0 text-white/70 hover:text-white p-1 -mr-1 -mt-1"
        aria-label="Kapat"
        data-testid="pwa-install-dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
}
